import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { transactionAPI, categoryAPI } from '../services/api';
import { Transaction, Category } from '../types';
import { useDateFormat } from '../contexts/DateFormatContext';
import './TransactionForm.css';
import 'antd/dist/reset.css';

const TransactionForm: React.FC = () => {
  const { t } = useTranslation();
  const { formatDate, parseDate, getDateInputFormat, dateFormat } = useDateFormat();
  
  // Convert dateFormat to antd format
  const getAntdDateFormat = () => {
    switch (dateFormat) {
      case 'mm-dd-yyyy':
        return 'MM-DD-YYYY';
      case 'yyyy-mm-dd':
        return 'YYYY-MM-DD';
      case 'dd-mm-yyyy':
      default:
        return 'DD-MM-YYYY';
    }
  };
  const [formData, setFormData] = useState({
    type: 'expense' as 'income' | 'expense',
    categoryId: '',
    amount: '',
    description: '',
    date: formatDate((() => {
      const now = new Date();
      now.setHours(12, 0, 0, 0);
      return now;
    })()),
    settlementFlag: null as number | null
  });
  const [dateValue, setDateValue] = useState(dayjs());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  useEffect(() => {
    loadCategories();
  }, [formData.type]);

  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const categoryData = await categoryAPI.getByType(formData.type);
      setCategories(categoryData);
    } catch (error) {
      console.error('カテゴリ取得エラー:', error);
      setMessage(t('category:categoryLoadError'));
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset categoryId when type changes
      ...(name === 'type' && { categoryId: '' })
    }));
    
    // Hide new category form when selecting existing category
    if (name === 'categoryId' && value !== 'new') {
      setShowNewCategoryForm(false);
      setNewCategoryName('');
    } else if (name === 'categoryId' && value === 'new') {
      setShowNewCategoryForm(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      if ((!formData.categoryId && !showNewCategoryForm) || !formData.amount || !formData.date) {
        setMessage(t('transaction:requiredFieldsError'));
        return;
      }
      
      if (showNewCategoryForm && !newCategoryName.trim()) {
        setMessage(t('category:categoryNameRequired'));
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        setMessage(t('transaction:invalidAmountError'));
        return;
      }

      let categoryId = formData.categoryId;
      
      // Create new category if needed
      if (showNewCategoryForm && newCategoryName.trim()) {
        const createResult = await categoryAPI.create({
          name: newCategoryName.trim(),
          type: formData.type
        });
        categoryId = createResult.id;
        
        // Create category object for local state
        const newCategory: Category = {
          id: createResult.id,
          name: newCategoryName.trim(),
          type: formData.type,
          createdAt: new Date().toISOString()
        };
        
        // Add new category to list for future use
        setCategories(prev => [...prev, newCategory]);
      }

      // Convert date to ISO format for API
      const parsedDate = parseDate(formData.date);
      if (!parsedDate) {
        setMessage(t('transaction:invalidDateError'));
        return;
      }
      
      const dateString = `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
      
      const transactionData: Omit<Transaction, 'id' | 'createdAt' | 'category'> = {
        type: formData.type,
        categoryId: categoryId,
        amount: amount,
        description: formData.description,
        date: dateString,
        settlementFlag: formData.settlementFlag
      };

      await transactionAPI.create(transactionData);
      setMessage(t('transaction:transactionAdded'));
      
      setFormData({
        type: 'expense',
        categoryId: '',
        amount: '',
        description: '',
        date: formatDate((() => {
          const now = new Date();
          now.setHours(12, 0, 0, 0);
          return now;
        })()),
        settlementFlag: null
      });
      setDateValue(dayjs());
      
      // Reset new category form
      setShowNewCategoryForm(false);
      setNewCategoryName('');
    } catch (error) {
      console.error('取引追加エラー:', error);
      setMessage(t('transaction:transactionAddError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setMessage(t('category:categoryNameRequired'));
      return;
    }

    setIsCreatingCategory(true);
    try {
      const createResult = await categoryAPI.create({
        name: newCategoryName.trim(),
        type: formData.type
      });
      
      // Create category object for local state
      const newCategory: Category = {
        id: createResult.id,
        name: newCategoryName.trim(),
        type: formData.type,
        createdAt: new Date().toISOString()
      };
      
      // Add new category to list
      setCategories(prev => [...prev, newCategory]);
      
      // Select the new category
      setFormData(prev => ({ ...prev, categoryId: newCategory.id }));
      
      // Reset form
      setShowNewCategoryForm(false);
      setNewCategoryName('');
      setMessage(t('category:categoryCreated'));
    } catch (error) {
      console.error('カテゴリ作成エラー:', error);
      setMessage(t('category:categoryCreateError'));
    } finally {
      setIsCreatingCategory(false);
    }
  };

  return (
    <div className="transaction-form">
      <h2>{t('transaction:addTransaction')}</h2>
      
      {message && (
        <div className={`message ${message.includes('エラー') || message.includes('Error') || message.includes('失敗') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label htmlFor="type" style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:type')} ：</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            style={{ 
              flex: 1, 
              maxWidth: '300px', 
              width: 'auto',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              boxSizing: 'border-box',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            <option value="expense">{t('transaction:expense')}</option>
            <option value="income">{t('transaction:income')}</option>
          </select>
        </div>

        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label htmlFor="categoryId" style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:category')} ：</label>
          <select
            id="categoryId"
            name="categoryId"
            value={showNewCategoryForm ? 'new' : formData.categoryId}
            onChange={handleChange}
            required
            disabled={isLoadingCategories}
            style={{ 
              flex: 1, 
              maxWidth: '300px', 
              width: 'auto',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              boxSizing: 'border-box',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            <option value="">
              {isLoadingCategories ? t('category:loadingCategories') : t('transaction:selectCategory')}
            </option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
            <option value="new">{t('category:addNewCategory')}</option>
          </select>
        </div>

        {showNewCategoryForm && (
          <div className="new-category-form" style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={t('category:enterCategoryName')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateCategory();
                  }
                }}
                style={{ 
                  flex: 1,
                  minWidth: '200px',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'rgba(255, 255, 255, 0.9)'
                }}
              />
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={isCreatingCategory || !newCategoryName.trim()}
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                {isCreatingCategory ? t('category:creating') : t('common:add')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewCategoryForm(false);
                  setNewCategoryName('');
                  setFormData(prev => ({ ...prev, categoryId: '' }));
                }}
                className="btn"
                style={{ whiteSpace: 'nowrap' }}
              >
                {t('common:cancel')}
              </button>
            </div>
          </div>
        )}

        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label htmlFor="amount" style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:amount')} ：</label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder={t('transaction:enterAmount')}
            min="0"
            step="0.01"
            required
            style={{ 
              flex: 1, 
              maxWidth: '300px', 
              width: 'auto',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              boxSizing: 'border-box',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'rgba(255, 255, 255, 0.9)'
            }}
          />
        </div>

        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('common:date')} ：</label>
          <DatePicker
            value={dateValue}
            format={getAntdDateFormat()}
            onChange={(date) => {
              setDateValue(date);
              if (date) {
                setFormData(prev => ({
                  ...prev,
                  date: formatDate(new Date(date.year(), date.month(), date.date(), 12, 0, 0, 0))
                }));
              }
            }}
            style={{ 
              flex: 1, 
              maxWidth: '300px', 
              height: '48px',
              borderRadius: '12px',
              border: '2px solid #e2e8f0'
            }}
          />
        </div>

        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label htmlFor="description" style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('common:description')} ：
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={t('transaction:enterDescription')}
            rows={2}
            style={{
              flex: 1,
              maxWidth: '300px',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              boxSizing: 'border-box',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'rgba(255, 255, 255, 0.9)',
              resize: 'vertical'
            }}
          />
        </div>

        <div className="horizontal-field" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <label htmlFor="settlementFlag" style={{ minWidth: '120px', textAlign: 'right', marginBottom: '0', flexShrink: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('transaction:settlementFlag')} ：</label>
          <select
            id="settlementFlag"
            name="settlementFlag"
            value={formData.settlementFlag || ''}
            onChange={(e) => {
              const value = e.target.value;
              setFormData(prev => ({
                ...prev,
                settlementFlag: value === '' ? null : parseInt(value)
              }));
            }}
            style={{ 
              flex: 1, 
              maxWidth: '300px', 
              width: 'auto',
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '16px',
              boxSizing: 'border-box',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            <option value="">{t('transaction:normal')}</option>
            <option value="1">{t('transaction:unsettled')}</option>
            <option value="9">{t('transaction:settled')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button 
            type="submit" 
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? t('transaction:adding') : t('common:add')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TransactionForm;